import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  ChangeEvent,
} from 'react';

import {
  Container,
  AlteredContent,
  FloatInput,
  Header,
} from 'styles/sgo_wrappers';

import SGOHeader from 'components/SGOHeader';
import SGONavbar from 'components/SGONavbar';
import Loading from 'components/Loading';
import ScrollTop from 'components/ScrollTop';
import SGOFooter from 'components/SGOFooter';
import RadioContainer from 'components/RadioContainer';

import { useCredentials } from 'hooks/credentials';
import {
  getLocalStorage,
  localStorageLength,
  setLocalStorage,
} from 'utils/handleLocalStorage';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import api from 'services/api';
import { useAuth } from 'hooks/auth';
import { removeAcento } from 'utils/specialChars';
import { formatDate } from 'utils/formatDate';
import { formatNumber } from 'utils/calcTotal';
import * as O from 'styles/option_buttons';
import { Link, useLocation } from 'react-router-dom';
import { FaArrowCircleUp, FaDollarSign, FaSearch } from 'react-icons/fa';
import { Grid, GridItem, InvertButton } from './styles';

interface LocalStorageProps {
  stat?: string;
}

interface ListProps {
  num: number;
  anb: string;
  anbdesc: string;
  data: string;
  valor: number;
  status: string;
  statusdesc: string;
  obs: string;
  pednum: number;
  peddata: string;
}

const FinGD: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { errorHandling, handlePermission } = useCredentials();
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [isInverted, setIsInverted] = useState(false);

  const [status, setStatus] = useState(() => {
    const hasItem = localStorageLength(`${process.env.REACT_APP_FINGD}`);

    if (hasItem > 0) {
      const { stat }: LocalStorageProps = getLocalStorage(
        `${process.env.REACT_APP_FINGD}`,
      );

      if (stat) {
        return stat;
      }

      return 'E';
    }

    return 'E';
  });

  const [rawList, setRawList] = useState<ListProps[]>([]);
  const [list, setList] = useState<ListProps[]>([]);

  const getList = useCallback(async () => {
    try {
      const response = await api.get('/sgo/fin_gd_list.php');
      setRawList(response.data);
      setList(
        response.data.filter(
          (item: ListProps) =>
            (status === 'A' ? item : item.status === status) &&
            (user.perfil === 'NAC'
              ? item.anb === user.anbc
              : item.anb.substr(0, 2) === user.zoncod.substr(0, 2)),
        ),
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      errorHandling(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorHandling]);

  useEffect(() => {
    handlePermission(['ZON', 'NAC'], true);

    getList();
  }, [getList, handlePermission]);

  const handleSearch = useCallback(
    (val: string) => {
      setSearchValue(val);

      setList(
        rawList.filter((item: ListProps) => {
          return (
            item.num.toString().includes(val) ||
            removeAcento(item.anbdesc)
              .toLowerCase()
              .includes(removeAcento(val).toLowerCase())
          );
        }),
      );
    },
    [rawList],
  );

  const handleRadioClick = useCallback(() => {
    const stat = formRef.current?.getFieldValue('statFilter');

    setStatus(stat);

    setList(
      rawList.filter(
        (item: ListProps) =>
          (stat === 'A' ? item : item.status === stat) &&
          (user.perfil === 'NAC'
            ? item.anb === user.anbc
            : item.anb.substr(0, 2) === user.zoncod.substr(0, 2)),
      ),
    );

    setLocalStorage(`${process.env.REACT_APP_FINGD}`, { stat });
  }, [rawList, user.anbc, user.perfil, user.zoncod]);

  const handleInvert = useCallback(() => {
    setIsInverted((state) => !state);
    setList((state) => [...state].reverse());
    setRawList((state) => [...state].reverse());
  }, []);

  return (
    <Container>
      <ScrollTop />
      <Loading isLoading={loading} />
      <SGOHeader />
      <SGONavbar
        needFilter
        filterContent={
          <Form translate={undefined}
            ref={formRef}
            onSubmit={() => {
              return null;
            }}
          >
            <RadioContainer
              title="Status Financeiro"
              onClick={handleRadioClick}
              name="statFilter"
              content={[
                {
                  id: 'sP',
                  value: 'E',
                  label: 'Pendente',
                },
                {
                  id: 'sL',
                  value: 'P',
                  label: 'Liquidada',
                },
                {
                  id: 'sA',
                  value: 'A',
                  label: 'Todas',
                },
              ]}
              itemsStyle={{
                flexDirection: 'row',
              }}
              selected={status}
            />
          </Form>
        }
      />
      <Header>Controle Financeiro de Guias de Distribuição</Header>
      {user.perfil === 'ZON' && (
        <FloatInput $amount="100px">
          <input
            placeholder="Filtrar por Coordenação ou n° GD"
            value={searchValue}
            type="text"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleSearch(e.target.value);
            }}
          />
        </FloatInput>
      )}
      <AlteredContent $pixels={user.perfil !== 'ZON' ? '231px' : '281px'}>
        {!loading && list.length > 0 && (
          <InvertButton as="button"
            type="button"
            onClick={handleInvert}
            $isInverted={isInverted}
          >
            <p>Mais {isInverted ? 'recentes' : 'antigas'} primeiro</p>
            <FaArrowCircleUp />
          </InvertButton>
        )}
        <Grid>
          {list.map((item) => (
            <GridItem key={`${item.num}-${item.anb}`}>
              <p>
                N° {item.num} |&nbsp;
                {formatDate(item.data)}
              </p>
              <span>
                <p>
                  Valor da GD:&nbsp;
                  <strong>R$ {formatNumber(item.valor)}</strong>
                </p>
              </span>
              <span>
                <p>
                  Status:&nbsp;
                  <strong>{item.statusdesc}</strong>
                </p>
              </span>
              {user.perfil === 'ZON' && (
                <span>
                  <p>
                    Coord:&nbsp;
                    <strong>{item.anbdesc}</strong>
                  </p>
                </span>
              )}
              {item.pednum ? (
                <span>
                  <p>
                    Baseada na solicitação:&nbsp;
                    <strong>
                      N° {item.pednum} | {formatDate(item.peddata)}
                    </strong>
                  </p>
                </span>
              ) : null}
              <O.GridOptions>
                <O.Show>
                  <Link
                    to={{
                      pathname: `${pathname.replace(
                        'tesouraria/contfingd',
                        'material/gd',
                      )}/consult`,
                      state: {
                        gdnum: item.num,
                        anbcod: item.anb,
                      },
                    }}
                    title="Consultar"
                  >
                    <FaSearch />
                  </Link>
                </O.Show>
                <O.Financ
                  to={{
                    pathname: `${pathname}/detail`,
                    state: {
                      finHeader: {
                        num: item.num,
                        cod: item.anb,
                        desc: item.anbdesc,
                        date: item.data,
                        valor: item.valor,
                      },
                    },
                  }}
                >
                  <FaDollarSign />
                </O.Financ>
              </O.GridOptions>
            </GridItem>
          ))}
        </Grid>
      </AlteredContent>
      <SGOFooter />
    </Container>
  );
};

export default FinGD;
